import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { StepperMainTool } from 'modules/main/domain/utils/stepper-tool';
import { Player } from 'modules/players/domain/interfaces/player';
import { invalidatePlayersList } from 'modules/players/store/actions';
import { ApplicationStatus } from 'modules/teams/domain/enums/application-status';
import { TeamPlayer } from 'modules/teams/domain/interfaces/team-player';
import { findPlayerInSquad } from 'modules/teams/domain/utils/squad-duplicate';
import { addPlayerApplication } from 'modules/teams/store/championship-teams';
import { upsertTournamentPlayerApplication } from 'modules/teams/store/players/actions';
import { AppDispatch } from 'store/store';

/** Значения формы игрока (`PlayerForm`), приведённые к тому, что нужно мутации. */
export interface CreateTeamPlayerValues {
  id?: number;
  name: string;
  surname: string;
  middleName?: string;
  birthDate: string;
  height?: string;
  weight?: string;
  photo?: (File | string)[];
}

interface UseCreateTeamPlayerParams {
  tournamentId: number;
  teamId: number;
  /** Весь состав команды — и заведённые вручную игроки, и вступившие участники. */
  players: TeamPlayer[];
  /** Игрок создан и добавлен в команду: закрыть окно, перечитать список и т. п. */
  onCreated?: (player: Player) => void;
}

/**
 * Создание игрока сразу в команде — общая часть двух точек входа: экрана
 * `teams/:id/players/create` в карточке команды и модалки «Добавить игрока» в заявках.
 *
 * Обе делают одно и то же и обязаны делать это одинаково: проверить дубль по составу
 * до похода на бэкенд, назвать отказ своими словами, сбросить кэш списка игроков и
 * дорисовать заявку в сторе заявок соревнования. Расходится только то, что происходит
 * после успеха, — это `onCreated`.
 *
 * Возвращает `true`, если игрок создан: вызывающая сторона по этому признаку решает,
 * закрывать ли форму.
 */
export function useCreateTeamPlayer({ tournamentId, teamId, players, onCreated }: UseCreateTeamPlayerParams) {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState(false);

  const createPlayer = useCallback(
    async (values: CreateTeamPlayerValues): Promise<boolean> => {
      const photo = values.photo && values.photo[0] instanceof File ? (values.photo as File[]) : undefined;
      const height = values.height && !isNaN(Number(values.height)) ? Number(values.height) : undefined;
      const weight = values.weight && !isNaN(Number(values.weight)) ? Number(values.weight) : undefined;

      // Тот же игрок уже в составе — на бэкенд не ходим вовсе: он ответит
      // «User is already in this team», но запрос и ожидание пользователю ни к чему.
      const duplicate = findPlayerInSquad(players, {
        userId: values.id,
        name: values.name,
        surname: values.surname,
        middleName: values.middleName,
        birthDate: values.birthDate,
      });

      if (duplicate) {
        toast.error('Игрок уже в этой команде');
        return false;
      }

      setIsLoading(true);

      let data: Player;
      try {
        // Тост об отказе вешаем только на саму операцию: если упадёт постобработка
        // ниже (навигация, синхронизация кэша), пользователь не должен видеть
        // «Не удалось выполнить операцию» на успешно созданном игроке.
        data = await dispatch(
          upsertTournamentPlayerApplication({
            userId: values.id,
            teamId,
            tournamentId,
            name: values.name,
            surname: values.surname,
            middleName: values.middleName,
            birthDate: values.birthDate,
            height,
            weight,
            photo,
          }),
        ).unwrap();
      } catch (error) {
        if (error === 'User is already in this team') {
          toast.error('Игрок уже в этой команде');
        } else if (error === 'Tournament user created') {
          toast.error('Такой игрок уже существует');
        } else {
          toast.error('Не удалось выполнить операцию');
        }
        return false;
      } finally {
        setIsLoading(false);
      }

      // Тот же текст, что на странице создания игрока (`players/ui/pages/player-create-page.tsx`),
      // — операция одна и та же, отличается только точка входа.
      toast.success('Игрок успешно создан');

      StepperMainTool.setByPath('stepper:main:createPlayer', true);

      // Игрок создан — кэш списка /players устарел: сбрасываем тег перечня,
      // и страница списка перечитает его при следующем показе.
      dispatch(invalidatePlayersList());
      dispatch(
        addPlayerApplication({
          teamId,
          application: {
            teamId,
            player: {
              id: data.id,
              firstName: data.name,
              lastName: data.surname,
              middleName: data.middleName,
              birthDate: data.birthDate,
              height: data.height,
              weight: data.weight,
              logoUrl: data.tournamentAvatars?.[0]?.image?.externalUrl,
            },
            status: ApplicationStatus.PENDING,
            id: data.id,
            // Заявка создаётся прямо сейчас. Раньше здесь стояло `data.createdAt`,
            // но запрос игрока это поле не выбирал, и в заявку всегда попадал undefined.
            createdAt: new Date().toISOString(),
          },
        }),
      );

      onCreated?.(data);

      return true;
    },
    [dispatch, onCreated, players, teamId, tournamentId],
  );

  return { createPlayer, isLoading };
}
